/**
 * Pure survey-derivation helpers (§5). Given the well's survey stations (whose
 * trajectory columns tvd/ns/ew/dls are ALREADY server-computed — min-curvature
 * on the OXBO-x compute-server), derive the display KPIs + the plot series. The
 * FE never recomputes trajectory here; it only reads, orders, and shapes what
 * the server served, exactly like the cost/NPT screens read server roll-ups.
 */
import type { SurveyStationOut } from "@api/types";

/** Soft-warn threshold for dogleg severity (°/100 length), per §5. */
export const DLS_SOFT_WARN = 1.5;
/** Inclination validity window (°). */
export const INC_MAX = 120;
/** Azimuth validity window (°). */
export const AZI_MAX = 360;

export interface SurveyKpis {
  /** Deepest station MD (the current hole depth surveyed). */
  currentMd: number | null;
  /** TVD at the deepest station. */
  currentTvd: number | null;
  /** Max inclination across stations. */
  maxInc: number | null;
  /** Max dogleg severity across stations. */
  maxDls: number | null;
  /** Whether max DLS crosses the soft-warn threshold. */
  dlsWarn: boolean;
  /** Closure distance at the deepest station (√(ns²+ew²)). */
  closure: number | null;
  /** Closure azimuth at the deepest station (° from north, 0–360). */
  closureAzimuth: number | null;
}

/** Stations sorted ascending by MD (the display + plot order). */
export function sortByMd(stations: SurveyStationOut[]): SurveyStationOut[] {
  return [...stations].sort((a, b) => a.md - b.md);
}

/**
 * Closure = horizontal distance from surface to the station's N/E point;
 * closure azimuth = its compass bearing (0° = north, clockwise). Returns null
 * when the station has no computed displacement yet.
 */
export function closureOf(
  station: Pick<SurveyStationOut, "ns" | "ew">,
): { distance: number; azimuth: number } | null {
  const { ns, ew } = station;
  if (ns == null || ew == null) return null;
  const distance = Math.hypot(ns, ew);
  // atan2(east, north) → bearing clockwise from north; normalise to 0–360.
  const azimuth = ((Math.atan2(ew, ns) * 180) / Math.PI + 360) % 360;
  return { distance, azimuth };
}

/** Derive the KPI chip values from the (unordered) station list. */
export function surveyKpis(stations: SurveyStationOut[]): SurveyKpis {
  if (stations.length === 0) {
    return {
      currentMd: null,
      currentTvd: null,
      maxInc: null,
      maxDls: null,
      dlsWarn: false,
      closure: null,
      closureAzimuth: null,
    };
  }
  const ordered = sortByMd(stations);
  // Non-empty (guarded above), so the deepest station is defined.
  const deepest = ordered[ordered.length - 1] as SurveyStationOut;
  const incs = stations.map((s) => s.inc).filter((n): n is number => Number.isFinite(n));
  const dlss = stations.map((s) => s.dls).filter((n): n is number => Number.isFinite(n));
  const maxInc = incs.length ? Math.max(...incs) : null;
  const maxDls = dlss.length ? Math.max(...dlss) : null;
  const closure = closureOf(deepest);
  return {
    currentMd: deepest.md,
    currentTvd: deepest.tvd,
    maxInc,
    maxDls,
    dlsWarn: maxDls != null && maxDls >= DLS_SOFT_WARN,
    closure: closure?.distance ?? null,
    closureAzimuth: closure?.azimuth ?? null,
  };
}

/** A single trajectory point, keeping only stations with computed columns. */
export interface TrajectoryPoint {
  md: number;
  tvd: number;
  ns: number;
  ew: number;
  dls: number | null;
}

/**
 * The subset of stations that carry a full computed trajectory (tvd/ns/ew),
 * in MD order — the plot series. Freshly-pasted stations whose columns the
 * server hasn't filled yet are excluded from the plots (they still show in the
 * table as "auto").
 */
export function trajectoryPoints(stations: SurveyStationOut[]): TrajectoryPoint[] {
  return sortByMd(stations)
    .filter((s) => s.tvd != null && s.ns != null && s.ew != null)
    .map((s) => ({
      md: s.md,
      tvd: s.tvd as number,
      ns: s.ns as number,
      ew: s.ew as number,
      dls: s.dls,
    }));
}

export interface ValidationIssue {
  /** i18n message key describing the problem. */
  kind: "mdOrder" | "incRange" | "aziRange";
  /** The value that tripped it (for the message). */
  value: number;
}

/**
 * Validate a candidate new station against the existing (MD-ordered) list:
 * MD strictly increasing past the deepest, inc 0–120°, azi 0–360°. DLS is a
 * server-computed soft-warn, not a hard validation, so it is not checked here.
 */
export function validateStation(
  draft: { md: number; inc: number; azi: number },
  existing: SurveyStationOut[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const deepest = existing.length ? Math.max(...existing.map((s) => s.md)) : -Infinity;
  if (!(draft.md > deepest)) issues.push({ kind: "mdOrder", value: draft.md });
  if (draft.inc < 0 || draft.inc > INC_MAX) issues.push({ kind: "incRange", value: draft.inc });
  if (draft.azi < 0 || draft.azi > AZI_MAX) issues.push({ kind: "aziRange", value: draft.azi });
  return issues;
}
